import { MergeFlagFeature, useMergeFlag } from "./useMergeFlag";

/**
 * Resolves whether the Frigade Assistant should render for an organization.
 *
 * MERGE_FLAG_FRIGADE_ASSISTANT is the rollout flag; MERGE_FLAG_FRIGADE_ASSISTANT_EXCLUDED
 * is an opt-out list that overrides it. The opt-out needs its own flag because MergeFlag
 * rules only ever add to the active set — they can't subtract an org.
 *
 * Also accounts for the build-time config the widget needs, so every caller agrees on
 * whether the Assistant can actually run rather than only on whether it's permitted to.
 */
export const useFrigadeAssistantEnabled = (organizationId: string | undefined) => {
  const { enabled: rolloutEnabled, hasInitialized: rolloutInitialized } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_FRIGADE_ASSISTANT,
    organizationId,
  });
  const { enabled: exclusionEnabled, hasInitialized: exclusionInitialized } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_FRIGADE_ASSISTANT_EXCLUDED,
    organizationId,
  });

  // useMergeFlag reports an errored request as undefined and a resolved-off flag as
  // false, so only an explicit false clears the exclusion — a failed lookup fails closed.
  const isExcluded = exclusionEnabled !== false;

  // Without these the widget never loads (see FrigadeAssistant), so treating the flag
  // alone as "enabled" would leave ⌘K routing to an SDK that is never there.
  const isConfigured = Boolean(
    process.env.REACT_APP_FRIGADE_ASSISTANT_PUBLIC_KEY &&
      process.env.REACT_APP_FRIGADE_ASSISTANT_ID,
  );

  return {
    hasInitialized: rolloutInitialized && exclusionInitialized,
    enabled: Boolean(rolloutEnabled) && !isExcluded && isConfigured,
    // Lets callers keep an excluded org off other AI surfaces too, not just this one.
    excluded: isExcluded,
  };
};
